import {
  forwardRef,
  useLayoutEffect,
  useRef,
  type ChangeEvent,
  type InputHTMLAttributes,
} from "react";

import {
  formatCurrencyInputValue,
  sanitizeNumericValue,
} from "@/components/clients/client-input-format";
import { cn } from "@/lib/utils";

export type ClientInputVariant = "text" | "email" | "currency" | "number";

export type ClientInputProps = {
  variant?: ClientInputVariant;
  /** Currency prefix for `currency` variant (e.g. £ / $). Defaults to £. */
  currencySymbol?: string;
  /** Optional validation message shown under the field. */
  error?: string;
} & Omit<InputHTMLAttributes<HTMLInputElement>, "type">;

const variantDefaults: Record<
  ClientInputVariant,
  {
    type: InputHTMLAttributes<HTMLInputElement>["type"];
    inputMode?: InputHTMLAttributes<HTMLInputElement>["inputMode"];
    placeholder: string;
    prefix?: string;
  }
> = {
  text: {
    type: "text",
    placeholder: "Enter a value",
  },
  email: {
    type: "email",
    inputMode: "email",
    placeholder: "e.g. name@company.com",
  },
  currency: {
    type: "text",
    inputMode: "decimal",
    placeholder: "e.g. 1,000",
    prefix: "£",
  },
  number: {
    type: "text",
    inputMode: "numeric",
    placeholder: "e.g. 5",
  },
};

function countNumericChars(value: string): number {
  return (value.match(/[\d.]/g) ?? []).length;
}

function caretFromNumericCount(
  formatted: string,
  numericCount: number,
): number {
  if (numericCount <= 0) return 0;

  let seen = 0;
  for (let i = 0; i < formatted.length; i += 1) {
    if (/[\d.]/.test(formatted[i] ?? "")) {
      seen += 1;
      if (seen === numericCount) return i + 1;
    }
  }

  return formatted.length;
}

/**
 * Dark TCI client text field (Figma 370:425) with email / currency / number variants.
 */
export const ClientInput = forwardRef<HTMLInputElement, ClientInputProps>(
  function ClientInput(
    {
      variant = "text",
      currencySymbol,
      className,
      placeholder,
      onChange,
      value,
      error,
      id,
      ...props
    },
    ref,
  ) {
    const defaults = variantDefaults[variant];
    const prefix =
      variant === "currency"
        ? (currencySymbol ?? defaults.prefix)
        : defaults.prefix;
    const showPrefix = Boolean(prefix);
    const innerRef = useRef<HTMLInputElement | null>(null);
    const pendingCaret = useRef<number | null>(null);
    const errorId = id ? `${id}-error` : undefined;

    const displayValue =
      variant === "currency" && typeof value === "string"
        ? formatCurrencyInputValue(value)
        : value;

    useLayoutEffect(() => {
      if (pendingCaret.current === null) return;
      const node = innerRef.current;
      if (node) {
        node.setSelectionRange(pendingCaret.current, pendingCaret.current);
      }
      pendingCaret.current = null;
    }, [displayValue]);

    function assignRef(node: HTMLInputElement | null) {
      innerRef.current = node;
      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    }

    function handleChange(event: ChangeEvent<HTMLInputElement>) {
      if (variant === "number") {
        const next = sanitizeNumericValue(event.target.value, variant);
        if (next !== event.target.value) {
          event.target.value = next;
        }
      }

      if (variant === "currency") {
        const caret = event.target.selectionStart ?? event.target.value.length;
        const numericBefore = countNumericChars(
          event.target.value.slice(0, caret),
        );
        const next = formatCurrencyInputValue(event.target.value);
        event.target.value = next;
        pendingCaret.current = caretFromNumericCount(next, numericBefore);
      }

      onChange?.(event);
    }

    return (
      <div className={cn("flex w-full flex-col gap-1.5", className)}>
        <div
          className={cn(
            "flex w-full items-center border bg-[rgba(11,22,40,0.42)] py-3 backdrop-blur-[8px]",
            error
              ? "border-red-400/70 focus-within:border-red-400"
              : "border-sky-400/35 focus-within:border-sky-400",
          )}
        >
          {showPrefix ? (
            <span
              aria-hidden
              className="pl-5 font-['Inter'] text-sm font-medium leading-5 tracking-[-0.02188rem] text-[#f5f3ee]"
            >
              {prefix}
            </span>
          ) : null}
          <input
            {...props}
            id={id}
            ref={assignRef}
            type={defaults.type}
            inputMode={defaults.inputMode}
            placeholder={placeholder ?? defaults.placeholder}
            {...(value !== undefined ? { value: displayValue } : {})}
            aria-invalid={error ? true : undefined}
            aria-describedby={error && errorId ? errorId : undefined}
            className={cn(
              "w-full min-w-0 bg-transparent font-['Inter'] text-sm font-medium not-italic leading-5 tracking-[-0.02188rem] text-[#f5f3ee]",
              "placeholder:text-white/50",
              "outline-none ring-0 ring-offset-0",
              "focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0",
              showPrefix ? "px-2 pr-5" : "px-5",
            )}
            onChange={handleChange}
          />
        </div>
        {error ? (
          <p
            id={errorId}
            role="alert"
            className="font-['Inter'] text-xs font-normal leading-4 tracking-[-0.2px] text-red-400"
          >
            {error}
          </p>
        ) : null}
      </div>
    );
  },
);
